import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    contactName: '',
    inviteToken: searchParams.get('token') || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await register({
      email: form.email,
      password: form.password,
      companyName: form.companyName,
      contactName: form.contactName,
      inviteToken: form.inviteToken,
    });

    if (result.success) {
      navigate('/store', { replace: true });
    } else {
      setError(result.error || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 animate-fade-in">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/FormalLogoFinal-Square.png" alt="Runninglime" className="h-16 w-16 mx-auto mb-4 rounded-xl" />
          <h1 className="text-3xl font-bold text-dark">Create Account</h1>
          <p className="text-gray-500 mt-2">Register your business to start ordering</p>
        </div>

        <div className="glass rounded-2xl p-8 shadow-xl animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="inviteToken" className="block text-sm font-medium text-dark mb-1.5">
                Registration Token
              </label>
              <input
                id="inviteToken"
                type="text"
                required
                value={form.inviteToken}
                onChange={update('inviteToken')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white font-mono text-sm"
                placeholder="Paste your registration token"
              />
              <p className="text-xs text-gray-400 mt-1">You need a token from an administrator to register</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-dark mb-1.5">
                  Company Name
                </label>
                <input
                  id="companyName"
                  type="text"
                  required
                  value={form.companyName}
                  onChange={update('companyName')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-dark mb-1.5">
                  Your Name
                </label>
                <input
                  id="contactName"
                  type="text"
                  required
                  value={form.contactName}
                  onChange={update('contactName')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark mb-1.5">
                Business Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={update('email')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={update('password')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="Min. 8 characters"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark mb-1.5">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={form.confirmPassword}
                onChange={update('confirmPassword')}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-lime focus:ring-2 focus:ring-lime/30 outline-none transition-all bg-white"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient py-3 rounded-xl text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition-transform"
            >
              {loading ? 'Creating account...' : 'Create Business Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-green font-medium hover:text-green-dark transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
